import _ from "lodash";
import React from "react";
import i18next from "src/i18n/config";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useDispatch, useSelector } from "src/store";
import { fetchProfile, selectProfile } from "src/store/account";
import { selectCurrentWorkspaceId, setCurrentWorkspaceId } from "src/store/app";
import { selectIsUserCredentialValid } from "src/store/auth";

import { fetchGroups, fetchLicenses } from "@dashboard/devices/store";
import { Subscription } from "@dashboard/devices/types";
import { usePrefetch as usePrefetchFiles } from "@dashboard/files/api";
import { normalizeFilesQueryParams } from "@dashboard/files/api/helpers";
import { fetchProductBuilds, fetchProducts } from "@dashboard/products/store";
import { fetchProvisionings } from "@dashboard/provisioning/store";
import { usePrefetch as usePrefetchFdroids } from "@dashboard/repositories/api";
import { useAllWorkspaces, useCurrentWorkspace, useSubscriptions } from "@dashboard/workspaces/api/helpers";
import { getDefaultProduct } from "@products/utils/getDefaultProduct/getDefaultProduct";
import UserSettingsService from "@services/userSettingsService";

import { showErrorToast } from "../shared/toasts/Toasts";

import "@services/timeService/timeService";

export function getDefaultSubscription(subscriptions: Subscription[]) {
    const mostRecentActive = _.orderBy(
        subscriptions.filter((subscription) => subscription.status === "Active"),
        ["creationDate"],
        ["desc"],
    )[0];

    return mostRecentActive ?? subscriptions[0];
}

const usePrefetchWorkspacesQuery = () => {
    const isUserCredentialValid = useSelector(selectIsUserCredentialValid);

    const { data: subscriptions } = useSubscriptions({}, { skip: !isUserCredentialValid });
    const defaultSubscription = getDefaultSubscription(subscriptions);

    const { data: workspaces, isFetching: isWorkspacesFetching } = useAllWorkspaces({}, { skip: !isUserCredentialValid });
    const defaultWorkspace = workspaces?.find((workspace) => workspace.subscription.id === defaultSubscription?.id);

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { data: currentWorkspace, isFetching: isCurrentWorkspaceFetching } = useCurrentWorkspace();

    const isWorkspaceIdWrong = workspaceId && !currentWorkspace && !isCurrentWorkspaceFetching;
    const noCurrentWorkspace = !workspaceId && !isWorkspacesFetching;

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (noCurrentWorkspace && defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(defaultWorkspace.id));
        } else if (noCurrentWorkspace && !defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(null));
        } else if (isWorkspaceIdWrong && defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(defaultWorkspace.id));
        } else if (isWorkspaceIdWrong && !defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(null));
        }
    }, [isWorkspaceIdWrong, noCurrentWorkspace, defaultWorkspace, workspaceId, dispatch]);

    // Workspace resolution is pending when workspaces are still loading,
    // or when we have a default workspace ready but the workspace ID
    // hasn't been set in the store yet (useEffect hasn't fired).
    const isResolvingWorkspace = isWorkspacesFetching || isCurrentWorkspaceFetching || (noCurrentWorkspace && !!defaultWorkspace);

    return { isResolvingWorkspace };
};

const useFetchProfile = () => {
    const [progress, setProgress] = React.useState(0);
    const [hasPrefetchError, setHasPrefetchError] = React.useState(false);

    const dispatch = useDispatch();
    const userCredentialValid = useSelector(selectIsUserCredentialValid);

    React.useLayoutEffect(() => {
        if (!userCredentialValid) {
            return;
        }

        void dispatch(fetchProfile())
            .unwrap()
            .catch(() => {
                setHasPrefetchError(true);
            })
            .finally(() => setProgress((prev) => prev + 100));

        return () => setProgress(0);
    }, [dispatch, userCredentialValid]);

    return { progress, hasPrefetchError };
};

const usePrefetchFilesQuery = () => {
    const prefetchUser = usePrefetchFiles("getFiles");

    const isUserCredentialValid = useSelector(selectIsUserCredentialValid);
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();

    React.useEffect(() => {
        if (isUserCredentialValid && workspaceId && !isPageFlagsLoading && effectiveWorkspaceSettings.isFilesPageEnabled) {
            prefetchUser(normalizeFilesQueryParams(UserSettingsService.getFilePageParams(), workspaceId));
        }
    }, [isUserCredentialValid, workspaceId, isPageFlagsLoading, effectiveWorkspaceSettings.isFilesPageEnabled, prefetchUser]);
};

const useFetchGroups = () => {
    const [progress, setProgress] = React.useState(0);
    const [hasPrefetchError, setHasPrefetchError] = React.useState(false);

    const dispatch = useDispatch();
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;

    const profile = useSelector(selectProfile);
    const isProfileCompleted = profile && profile.profileCompleted;

    React.useLayoutEffect(() => {
        if (isProfileCompleted && !workspaceId) {
            setProgress(100);
        }
        if (isProfileCompleted && workspaceId) {
            void dispatch(fetchGroups(workspaceId))
                .unwrap()
                .then(() => {
                    setProgress((prev) => prev + 50);
                    return dispatch(
                        fetchLicenses({ groupId: workspace.subscription.groupIds, isPrefetch: true, pageSize: 48 }),
                    ).unwrap();
                })
                .then(() => {
                    setProgress((prev) => prev + 50);
                    return dispatch(fetchLicenses({ groupId: workspace.subscription.groupIds })).unwrap();
                })
                .catch(() => {
                    setHasPrefetchError(true);
                });
        }
        return () => setProgress(0);
    }, [dispatch, workspaceId, isProfileCompleted]);

    return { progress, hasPrefetchError };
};

const useFetchFdroids = () => {
    const prefetchFdroids = usePrefetchFdroids("getFdroids");
    const isUserCredentialValid = useSelector(selectIsUserCredentialValid);
    const { data: workspace } = useCurrentWorkspace();
    const subscriptionId = workspace?.subscription.id;

    React.useLayoutEffect(() => {
        if (isUserCredentialValid && subscriptionId) {
            prefetchFdroids({ subscriptionId });
        }
    }, [isUserCredentialValid, subscriptionId]);
};

const useFetchProducts = () => {
    const [progress, setProgress] = React.useState(0);
    const [hasPrefetchError, setHasPrefetchError] = React.useState(false);

    const dispatch = useDispatch();
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription.status === "Terminated";
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();

    const profile = useSelector(selectProfile);
    const isProfileCompleted = profile && profile.profileCompleted;

    React.useLayoutEffect(() => {
        if (isProfileCompleted && !workspaceId) {
            setProgress(100);
        }

        if (isProfileCompleted && workspaceId && !isPageFlagsLoading) {
            const fetchWithPriority = async () => {
                const products = await dispatch(fetchProducts({ workspaceId: workspace.id, isTerminated })).unwrap();
                const defaultProduct = getDefaultProduct(products);

                if (defaultProduct) {
                    await dispatch(fetchProductBuilds(defaultProduct.id)).unwrap();
                    const remainingProducts = products.filter((p) => p.id !== defaultProduct.id);
                    for (const product of remainingProducts) {
                        void dispatch(fetchProductBuilds(product.id))
                            .unwrap()
                            .catch(() => {
                                showErrorToast(i18next.t("toast.errorFetchingProductBuilds"));
                            });
                    }
                }
            };

            void fetchWithPriority()
                .catch(() => setHasPrefetchError(true))
                .finally(() => {
                    setProgress((prev) => prev + 50);
                    if (!effectiveWorkspaceSettings.isProvisioningPageEnabled) {
                        setProgress((prev) => prev + 50);
                    }
                });

            if (effectiveWorkspaceSettings.isProvisioningPageEnabled) {
                void dispatch(fetchProvisionings({ workspaceId: workspace.id, isTerminated }))
                    .unwrap()
                    .catch(() => setHasPrefetchError(true))
                    .finally(() => setProgress((prev) => prev + 50));
            }
        }
        return () => setProgress(0);
    }, [
        dispatch,
        workspaceId,
        isTerminated,
        isProfileCompleted,
        isPageFlagsLoading,
        effectiveWorkspaceSettings.isProvisioningPageEnabled,
    ]);

    return { progress, hasPrefetchError };
};

export const usePrefetch = () => {
    const { isResolvingWorkspace } = usePrefetchWorkspacesQuery();
    const { progress: productProgress, hasPrefetchError: productError } = useFetchProducts();

    usePrefetchFilesQuery();
    useFetchFdroids();

    const { progress: groupProgress, hasPrefetchError: groupError } = useFetchGroups();
    const { progress: profileProgress, hasPrefetchError: profileError } = useFetchProfile();

    const stages = [groupProgress, productProgress, profileProgress];

    const hasPrefetchError = groupError || productError || profileError;

    return {
        progress: _.sum(stages) / stages.length,
        hasPrefetchError,
        isResolvingWorkspace,
    };
};
